import React, { useEffect, useRef, useState } from "react";
import { Button } from "../../components/button/Button";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { Col, Form } from "react-bootstrap";
import { apiRequest } from "../../services";
import { toast } from "react-toastify";
import "./userInfo.css";
import { useDispatch, useSelector } from "react-redux";
import { ThreeCircles } from "react-loader-spinner";
import Images from "../../constants/Images";
import { useTranslation } from "react-i18next";
import {useNavigate} from "react-router-dom";

const UserInfo = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({});
  const userData = useSelector((state) => state.users);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [idCardFront, setIdCardFront] = useState(null);
  const [idCardBack, setIdCardBack] = useState(null);
  const [image, setImage] = useState(null);
  const hiddenFileInput = useRef(null);
  const [formErrors, setFormErrors] = useState({});
  const [isUnderage, setIsUnderage] = useState(false);

  const [formBenefData, setFormBenefData] = useState({});

  useEffect(() => {
    if (formBenefData && formBenefData.dateOfBirth) {
      const birthDate = dayjs(formBenefData.dateOfBirth);
      const age = dayjs().diff(birthDate, "year");
      setIsUnderage(age < 18);
    }
  }, [formBenefData]);

  const handleEdit = async () => {
    try {
      setLoading(true);
      const response = await apiRequest(
        "AP",
        `api/v1/UpdateClient/${userData.users.clientDetails.phoneNumber}`,
        "PUT",
        formData,
        null,
        dispatch,
        navigate
      );
      if (response && response.status === 200) {
        toast.success(t("clientUpdated"));
      } else {
        toast.error(response.body.message || t("editingFailed"));
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error(t("editingFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleEditBenef = async () => {
    try {
      setLoading(true);
      const response = await apiRequest(
        "CLIENT",
        `api/techcare/v2/clientBeneficiary/updateClientBeneficial`,
        "PUT",
        formBenefData,
        null,
        dispatch,
        navigate
      );
      if (response && response.status === 200) {
        toast.success(t("clientUpdated"));
      } else {
        toast.error(response.body.errors || t("editingFailed"));
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error(t("editingFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleEditCombined = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      const formDataWithImages = new FormData();
      for (const key in formData) {
        formDataWithImages.append(key, formData[key]);
      }
      if (idCardFront) formDataWithImages.append("file", idCardFront);
      if (idCardBack) formDataWithImages.append("file2", idCardBack);

      const apiUrl = `/api/techcare/v2/client/uploadNationalId/${userData.users.clientDetails.phoneNumber}`;
      const uploadResponse =
        idCardFront || idCardBack
          ? await apiRequest("AP", apiUrl, "POST", formDataWithImages, null,
            dispatch,
            navigate)
          : null;

      if (uploadResponse && uploadResponse.status !== 200) {
        toast.error(uploadResponse.body.title || t("failedIdCardUpload"));
        setLoading(false);
        return;
      }

      await Promise.all([handleEdit(), handleEditBenef()]);
      setIdCardFront(null);
      setIdCardBack(null);
    } catch (error) {
      console.error("Error in one of the API calls:", error);
      toast.error(t("editingFailed"));
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.fullName) errors.fullName = t("requiredField");
    else if (formData.fullName.length < 2)
      errors.fullName = t("minimumCharacters", { count: 2 });
    if (!formData.dateOfBirth) errors.dateOfBirth = t("requiredField");
    else {
      const birthDate = dayjs(formData.dateOfBirth, "DD/MM/YYYY");
      const age = dayjs().diff(birthDate, "year");
      if (age < 18) errors.dateOfBirth = t("minimumAge", { age: 18 });
      else if (age >= 65) errors.dateOfBirth = t("maximumAge", { age: 65 });
    }
    if (!formData.gender) errors.gender = t("requiredField");
    if (!formData.idIssueDate) errors.idIssueDate = t("requiredField");
    if (!formData.idPlaceOfIssue) errors.idPlaceOfIssue = t("requiredField");
    if (!formData.occupation) errors.occupation = t("requiredField");
    if (!formData.maritalStatus) errors.maritalStatus = t("requiredField");
    if (!formData.phoneNumber) errors.phoneNumber = t("requiredField");
    if (!formData.email) errors.email = t("requiredField");
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      errors.email = t("invalidEmail");
    if (isUnderage) {
      if (!formBenefData.guardianName) errors.guardianName = t("requiredField");
      else if (formBenefData.guardianName.length < 2)
        errors.guardianName = t("minimumCharacters", { count: 2 });
      if (!formBenefData.guardianRelationship)
        errors.guardianRelationship = t("requiredField");
      if (!formBenefData.guardianPhoneNumber)
        errors.guardianPhoneNumber = t("requiredField");
      else if (formBenefData.guardianPhoneNumber.length !== 9)
        errors.guardianPhoneNumber = t("phoneNumberLength");
    }
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) toast.error(t("fillAllFields"));
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e, field = null, date = null) => {
    if (date) {
      const formattedDate = dayjs(date).format("DD/MM/YYYY");
      setFormData((prevData) => ({ ...prevData, [field]: formattedDate }));
    } else if (e && e.target) {
      const { name, value } = e.target;
      setFormData((prevData) => ({ ...prevData, [name]: value }));
    }
  };

  // const handleBenefChange = (e, date = null) => {
  //   const { name, value } = e.target;
  //   setFormBenefData((prevData) => ({
  //     ...prevData,
  //     [name]: value,
  //     insuranceId: userData.users.insuranceCode,
  //   }));
  // };

  const handleIdCardChange = (e) => {
    const file = e.target.files[0];
    const idCardSide = e.target.name;
    if (idCardSide === "front") setIdCardFront(file);
    else if (idCardSide === "back") setIdCardBack(file);
  };

  const handleClick = () => hiddenFileInput.current.click();
  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setImage(file);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      const img = new Image();
      img.src = reader.result;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const maxSize = Math.max(img.width, img.height);
        canvas.width = maxSize;
        canvas.height = maxSize;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(
          img,
          (maxSize - img.width) / 2,
          (maxSize - img.height) / 2
        );
        canvas.toBlob(
          (blob) => {
            const compressedFile = new File([blob], file.name, {
              type: "image/jpeg",
              lastModified: Date.now(),
            });
            setImage(compressedFile);
            UploadClientPhoto(compressedFile);
          },
          "image/jpeg",
          0.8
        );
      };
    };
  };

  const UploadClientPhoto = async (file) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("file", file);
      const apiUrl = `api/techcare/v2/client/uploadClientProfilePhoto/${userData.users.clientDetails.phoneNumber}`;
      const response = await apiRequest("CLIENT", apiUrl, "POST", formData, null,
        dispatch,
        navigate);
      console.log("response is ", response);
      if (response && response.status === 200) {
        let photoUrl = response.body.data.profilePhoto;
        dispatch({
          type: "UPDATE_PROFILE_PICTURE",
          value: response.body.data.profilePhoto,
        });
         dispatch({
          type: "SET_CLIENT_DETAILS",
          value: response.body.data,
        });
        if (!photoUrl.startsWith("https://")) {
          const fileName = photoUrl.split("\\").pop();
          photoUrl = `https://clientms.credixcm.com/image/imageType/ClientPhoto/${fileName}`;
        }
        setImage(file);
      } else {
        toast.error("Failed Photo Upload!!! Try Again");
      }
    } catch (error) {
      console.error("Error:", error);
      setImage(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchClientInfo = async () => {
      try {
        const requestData = {
          serviceReference: "GET_CLIENT_BY_INSURANCE_CODE_WITH_BENEFICIARY",
          requestBody: userData.users.clientDetails.clientId,
        };
        setLoading(true);
        const response = await apiRequest(
          "GATEWAY",
          "",
          "POST",
          requestData,
          userData.token,
          dispatch,
          navigate
        );
        if (response && response.status === 200) {
          setFormData(response.body.data.client);
          setFormBenefData(response.body.data.clientBeneficial || {});
        }
        console.log("response is ", response);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchClientInfo();
  }, []);

  return (
    <div className="monprofil-container">
      <div className="monprofil-header">
        <div className="monprofil-overlay-text animate__animated animate__fadeIn">
          {t("UserProfile")}
        </div>
      </div>
      <div className="monprofil-content">
        <div className="monprofil-welcome">
          <h2 className="monprofil-welcome-title">
            Bienvenue, {formData.fullName || "Client"}
          </h2>
          <p className="monprofil-welcome-subtitle">
            Gérez vos informations et rejoignez une expérience innovante.
          </p>
        </div>
        <div className="monprofil-profile-card">
          <div className="monprofil-profile-image">
            <div onClick={handleClick} style={{ cursor: "pointer" }}>
              {image ? (
                <img
                  src={URL.createObjectURL(image)}
                  alt="Profile"
                  className="monprofil-avatar"
                />
              ) : userData?.users?.clientDetails?.profilePhoto ? (
                <img
                  src={userData?.users?.clientDetails?.profilePhoto}
                  alt="Profile"
                  className="monprofil-avatar"
                />
              ) : (
                <img
                  src={Images.avatar}
                  alt="Profile"
                  className="monprofil-avatar"
                />
              )}
              <input
                id="image-upload-input"
                type="file"
                onChange={handleImageChange}
                ref={hiddenFileInput}
                style={{ display: "none" }}
                required
              />
            </div>
            {loading && (
              <div className="monprofil-spinner">
                <ThreeCircles
                  width="40"
                  height="40"
                  radius="9"
                  color="#007bff"
                />
              </div>
            )}
          </div>
          <div className="monprofil-profile-details">
            <fieldset className="monprofil-fieldset">
              <legend className="monprofil-legend">{t("userInfo")}</legend>
              <Form className="monprofil-form">
                <Form.Group className="monprofil-form-group-full">
                  <Form.Label className="monprofil-label">
                    <i className="fas fa-user"></i> {t("name")}
                    <span className="monprofil-required">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="fullName"
                    value={formData.fullName || ""}
                    onChange={handleChange}
                    className={`monprofil-input ${
                      formErrors.fullName ? "monprofil-invalid" : ""
                    }`}
                    placeholder={t("namePlaceholder")}
                    required
                  />
                  {formErrors.fullName && (
                    <div className="monprofil-error">{formErrors.fullName}</div>
                  )}
                </Form.Group>
                <Form.Group className="monprofil-form-group-full">
                  <Form.Label className="monprofil-label">
                    <i className="fas fa-calendar"></i> {t("dateOfBirth")}
                    <span className="monprofil-required">*</span>
                  </Form.Label>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={
                        formData.dateOfBirth
                          ? dayjs(formData.dateOfBirth, "YYYY-MM-DD")
                          : null
                      }
                      onChange={(date) =>
                        handleChange(null, "dateOfBirth", date)
                      }
                      format="YYYY-MM-DD"
                      disableFuture
                      className={`monprofil-input ${
                        formErrors.dateOfBirth ? "monprofil-invalid" : ""
                      }`}
                      slotProps={{ textField: { required: true } }}
                    />
                  </LocalizationProvider>
                  {formErrors.dateOfBirth && (
                    <div className="monprofil-error">
                      {formErrors.dateOfBirth}
                    </div>
                  )}
                </Form.Group>
                <Form.Group className="monprofil-form-group-full">
                  <Form.Label className="monprofil-label">
                    <i className="fas fa-venus-mars"></i> {t("gender")}
                    <span className="monprofil-required">*</span>
                  </Form.Label>
                  <Form.Select
                    name="gender"
                    value={formData.gender || ""}
                    onChange={handleChange}
                    className={`monprofil-input ${
                      formErrors.gender ? "monprofil-invalid" : ""
                    }`}
                    required
                  >
                    <option value="" disabled>
                      {t("selectGender")}
                    </option>
                    <option value="Male">{t("male")}</option>
                    <option value="Female">{t("female")}</option>
                  </Form.Select>
                  {formErrors.gender && (
                    <div className="monprofil-error">{formErrors.gender}</div>
                  )}
                </Form.Group>
                <Form.Group className="monprofil-form-group-full">
                  <Form.Label className="monprofil-label">
                    <i className="fas fa-globe"></i> {t("nationality")}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="nationality"
                    value={formData.nationality || ""}
                    onChange={handleChange}
                    className="monprofil-input"
                    placeholder={t("nationalityPlaceholder")}
                  />
                </Form.Group>
                <Form.Group className="monprofil-form-group-full">
                  <Form.Label className="monprofil-label">
                    <i className="fas fa-id-card"></i> {t("nationalId")}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="nationalID"
                    value={formData.cniNumber || ""}
                    onChange={handleChange}
                    className="monprofil-input"
                    placeholder={t("nationalIdPlaceholder")}
                  />
                </Form.Group>
                <Form.Group className="monprofil-form-group-full">
                  <Form.Label className="monprofil-label">
                    <i className="fas fa-calendar-check"></i>{" "}
                    {t("cniIssueDate")}
                  </Form.Label>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={
                        formData.cniIssueDate
                          ? dayjs(formData.cniIssueDate, "YYYY-MM-DD")
                          : null
                      }
                      onChange={(date) =>
                        handleChange(null, "cniIssueDate", date)
                      }
                      format="YYYY-MM-DD"
                      disableFuture
                      className={`monprofil-input ${
                        formErrors.cniIssueDate ? "monprofil-invalid" : ""
                      }`}
                      slotProps={{ textField: { required: true } }}
                    />
                  </LocalizationProvider>
                  {formErrors.idIssueDate && (
                    <div className="monprofil-error">
                      {formErrors.idIssueDate}
                    </div>
                  )}
                </Form.Group>
                <Form.Group className="monprofil-form-group-full">
                  <Form.Label className="monprofil-label">
                    <i className="fas fa-map-marker-alt"></i>{" "}
                    {t("cniPlaceOfIssue")}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="idPlaceOfIssue"
                    value={formData.cniPlaceOfIssue || ""}
                    onChange={handleChange}
                    className={`monprofil-input ${
                      formErrors.cniPlaceOfIssue ? "monprofil-invalid" : ""
                    }`}
                    placeholder={t("cniPlaceOfIssuePlaceholder")}
                  />
                  {formErrors.cniPlaceOfIssue && (
                    <div className="monprofil-error">
                      {formErrors.cniPlaceOfIssue}
                    </div>
                  )}
                </Form.Group>
                <Form.Group className="monprofil-form-group-full">
                  <Form.Label className="monprofil-label">
                    <i className="fas fa-phone"></i> {t("phoneNumber")}
                    <span className="monprofil-required">*</span>
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber || ""}
                    onChange={handleChange}
                    className={`monprofil-input ${
                      formErrors.phoneNumber ? "monprofil-invalid" : ""
                    }`}
                    placeholder={t("phoneNumberPlaceholder")}
                    required
                  />
                  {formErrors.phoneNumber && (
                    <div className="monprofil-error">
                      {formErrors.phoneNumber}
                    </div>
                  )}
                </Form.Group>
                <Form.Group className="monprofil-form-group-full">
                  <Form.Label className="monprofil-label">
                    <i className="fas fa-envelope"></i> {t("email")}
                    <span className="monprofil-required">*</span>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email || ""}
                    onChange={handleChange}
                    className={`monprofil-input ${
                      formErrors.email ? "monprofil-invalid" : ""
                    }`}
                    placeholder={t("emailPlaceholder")}
                    required
                  />
                  {formErrors.email && (
                    <div className="monprofil-error">{formErrors.email}</div>
                  )}
                </Form.Group>
                <Form.Group className="monprofil-form-group-full">
                  <Form.Label className="monprofil-label">
                    <i className="fas fa-briefcase"></i> {t("occupation")}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="occupation"
                    value={formData.occupation || ""}
                    onChange={handleChange}
                    className={`monprofil-input ${
                      formErrors.occupation ? "monprofil-invalid" : ""
                    }`}
                    placeholder="Enter your occupation"
                  />
                  {formErrors.occupation && (
                    <div className="monprofil-error">
                      {formErrors.occupation}
                    </div>
                  )}
                </Form.Group>
                <Form.Group className="monprofil-form-group-full">
                  <Form.Label className="monprofil-label">
                    <i className="fas fa-heart"></i> {t("maritalStatus")}
                  </Form.Label>
                  <Form.Select
                    name="maritalStatus"
                    value={formData.maritalStatus || ""}
                    onChange={handleChange}
                    className={`monprofil-input ${
                      formErrors.maritalStatus ? "monprofil-invalid" : ""
                    }`}
                  >
                    <option value="" disabled>
                      {t("selectStatus")}
                    </option>
                    <option value="Single">{t("single")}</option>
                    <option value="Married">{t("married")}</option>
                    <option value="Divorced">{t("divorced")}</option>
                    <option value="Widowed">{t("widowed")}</option>
                  </Form.Select>
                  {formErrors.maritalStatus && (
                    <div className="monprofil-error">
                      {formErrors.maritalStatus}
                    </div>
                  )}
                </Form.Group>
                <Form.Group className="monprofil-form-group-full">
                  <Form.Label className="monprofil-label">
                    <i className="fas fa-upload"></i> {t("uploadCniFront")}
                  </Form.Label>
                  <Form.Control
                    type="file"
                    name="front"
                    onChange={handleIdCardChange}
                    className="monprofil-input"
                  />
                  {idCardFront && (
                    <div className="monprofil-id-card-display animate__animated animate__fadeIn">
                      <h3 className="monprofil-id-title">{t("idCardFront")}</h3>
                      <img
                        src={URL.createObjectURL(idCardFront)}
                        alt="ID Card Front"
                        className="monprofil-id-image"
                      />
                    </div>
                  )}
                </Form.Group>
                <Form.Group className="monprofil-form-group-full">
                  <Form.Label className="monprofil-label">
                    <i className="fas fa-upload"></i> {t("uploadCniBack")}
                  </Form.Label>
                  <Form.Control
                    type="file"
                    name="back"
                    onChange={handleIdCardChange}
                    className="monprofil-input"
                  />
                  {idCardBack && (
                    <div className="monprofil-id-card-display animate__animated animate__fadeIn">
                      <h3 className="monprofil-id-title">{t("idCardBack")}</h3>
                      <img
                        src={URL.createObjectURL(idCardBack)}
                        alt="ID Card Back"
                        className="monprofil-id-image"
                      />
                    </div>
                  )}
                </Form.Group>
              </Form>
            </fieldset>
            <div className="monprofil-actions">
              <Button onClick={handleEditCombined} className="monprofil-button">
                {loading ? (
                  <div className="monprofil-spinner-inline">
                    <ThreeCircles width="30" height="30" />
                    <span>{t("pleaseWait")}</span>
                  </div>
                ) : (
                  t("update")
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserInfo;
